import {
    monitorForElements
} from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { Box, FormControl, InputLabel, MenuItem, Select, Typography } from '@mui/material';
import { createContext, useCallback, useEffect, useState } from 'react';
import { Camera } from '../types';
import { supabase } from '../utils/supabase';
import CameraGridItem from './CameraGridItem';
import { sortByName } from '../utils/utils';

// Helper function: Returns true if the URL is non-empty and starts with http(s) or rtmp
function isValidHttpUrl(urlString: string): boolean {
    if (!urlString || urlString.trim() === '') return false;
    try {
        const url = new URL(urlString);
        return ['http:', 'https:', 'rtmp:'].includes(url.protocol);
    } catch (error) {
        return false;
    }
}

// Pragmatic DND
function getInstanceId() {
    return Symbol('instance-id');
}

export const InstanceIdContext = createContext<symbol | null>(null);
// END: Pragmatic DND

interface CameraScreenProps {
    userId: string;
    isAdmin: boolean;
}

export default function CameraScreen({ userId, isAdmin }: CameraScreenProps) {
    const [cameras, setCameras] = useState<Camera[]>([]);
    const [allCameras, setAllCameras] = useState<Camera[]>([]);
    const [selectedCameraIds, setSelectedCameraIds] = useState<string[]>([]);
    const [layout, setLayout] = useState<string[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [columns, setColumns] = useState<number>(4); // Default to 4 columns
    const [availableWidth, setAvailableWidth] = useState(window.innerWidth > 840 ? window.innerWidth - 240 : window.innerWidth);

    // Pragmatic DND
    const [instanceId] = useState(getInstanceId);

    useEffect(() => {
        return monitorForElements({
            canMonitor({ source }) {
                return source.data.instanceId === instanceId;
            },
            onDrop({ source, location }) {
                const dest = location.current.dropTargets[0];
                if (!dest) return;
                const fromId = source.data.id as string;
                const toId = dest.data.id as string;
                if (fromId === toId) return;

                // swap positions
                setCameras((prev) => {
                    const next = [...prev];
                    const i = next.findIndex((c) => c.id === fromId);
                    const j = next.findIndex((c) => c.id === toId);
                    [next[i], next[j]] = [next[j], next[i]];

                    const newLayout = next.map((c) => c.id);
                    setLayout(newLayout);
                    saveLayout(newLayout, columns);
                    return next;
                });
            },
        });
    }, [instanceId]);

    // calculate dynamic width
    useEffect(() => {
        const handleResize = () => {
            setAvailableWidth(window.innerWidth > 840 ? window.innerWidth - 240 : window.innerWidth);
        };

        window.addEventListener('resize', handleResize);

        // Cleanup the event listener on component unmount
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    // END: Pragmatic DND

    // Fetch cameras
    const fetchCameras = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        console.log('fetchCameras: isAdmin=', isAdmin, 'userId=', userId);

        try {
            if (isAdmin) {
                const { data, error } = await supabase.from('cameras').select('*');
                if (error) {
                    console.error('Error fetching all cameras:', error);
                    setError('Failed to fetch cameras: ' + error.message);
                    setAllCameras([]);
                    setCameras([]);
                    setIsLoading(false);
                    return;
                }

                // sort camera list
                const sortedCameras = sortByName(data);
                console.log('All cameras fetched:', sortedCameras);
                setAllCameras(sortedCameras);

                const { data: userData, error: userError } = await supabase
                    .from('users')
                    .select('selected_cameras, columns')
                    .eq('id', userId)
                    .single();
                if (userError) {
                    console.error('Error fetching user data:', userError);
                    setError('Failed to fetch user data: ' + userError.message);
                    setCameras([]);
                    setSelectedCameraIds([]);
                    setIsLoading(false);
                    return;
                }
                const selectedIds = Array.isArray(userData?.selected_cameras) ? userData.selected_cameras : [];
                console.log('Selected camera IDs:', selectedIds);
                setSelectedCameraIds(selectedIds);
                setCameras(sortedCameras.filter((camera) => selectedIds.includes(camera.id)));
                setColumns(userData?.columns || 4);
            } else {
                // customers
                const { data: userData, error: userError } = await supabase
                    .from('users_cameras')
                    .select(`cameras (
                        id,
                        name,
                        stream_url)
                        `)
                    .eq('user_id', userId);
                if (userError) {
                    console.error('Error fetching user data:', userError);
                    setError('Failed to fetch user data: ' + userError.message);
                    setCameras([]);
                    setIsLoading(false);
                    return;
                }

                const data = userData.flatMap(row => row.cameras ?? []);
                if (data.length > 0) {
                    console.log('Customer camera fetched:', data);
                    setCameras(data);
                }
                else {
                    console.log('No camera assigned for customer');
                    setCameras([]);
                }
            }
        } catch (err) {
            console.error('Unexpected error in fetchCameras:', err);
            setError('Unexpected error occurred');
        }
        setIsLoading(false);
    }, [userId, isAdmin]);

    // Fetch layout
    const fetchLayout = useCallback(async () => {
        try {
            const { data, error } = await supabase
                .from('users')
                .select('layout')
                .eq('id', userId)
                .single();
            if (error) {
                console.error('Error fetching layout:', error);
                setError('Failed to fetch layout: ' + error.message);
                return;
            }
            if (data?.layout) {
                console.log('Layout fetched:', data.layout);
                setLayout(data.layout);
            }
        } catch (err) {
            console.error('Unexpected error in fetchLayout:', err);
            setError('Unexpected error occurred');
        }
    }, [userId]);

    useEffect(() => {
        fetchCameras();
        fetchLayout();
    }, [fetchCameras, fetchLayout]);

    // Generate initial layout
    useEffect(() => {
        if (cameras.length > 0 && (layout.length === 0 || layout.length !== cameras.length)) {
            setLayout(layout);
            saveLayout(layout, columns);
        }
    }, [cameras, columns, layout.length]);

    // Save layout and columns
    const saveLayout = useCallback(
        async (newLayout: string[], columnsToSave: number) => {
            const updates: { layout: string[]; selected_cameras?: string[]; columns?: number } = {
                layout: newLayout,
            };
            if (isAdmin) {
                updates.selected_cameras = newLayout;
                updates.columns = columnsToSave;
            }

            console.log('Saving layout, columns, and selected cameras:', updates);
            try {
                const { error } = await supabase
                    .from('users')
                    .update(updates)
                    .eq('id', userId);

                if (error) {
                    console.error('Error saving layout:', error);
                    setError('Failed to save layout: ' + error.message);
                }
            } catch (err) {
                console.error('Unexpected error in saveLayout:', err);
                setError('Unexpected error occurred');
            }
        },
        [userId, isAdmin, cameras]
    );

    function generateLayout(selectedCameraIds: string[], prevLayout: string[]): string[] {
        // 1. keep the ones still selected, in their old order
        const kept = prevLayout.filter((id) => selectedCameraIds.includes(id));
        // 2. find newly added IDs
        const added = selectedCameraIds.filter((id) => !kept.includes(id));
        // 3. merge
        return [...kept, ...added];
    }

    // Handle camera selection
    const handleCameraSelection = async (event: any) => {
        const selectedIds = Array.isArray(event.target.value) ? event.target.value : [];
        console.log('Camera selection changed:', selectedIds);
        setSelectedCameraIds(selectedIds);
        const newCameras = allCameras.filter((camera) => selectedIds.includes(camera.id));
        setCameras(newCameras);

        // Generate new layout for all cameras
        const newLayout = generateLayout(selectedIds, layout);
        setLayout(newLayout);
        saveLayout(newLayout, columns);
    };

    // Handle column selection
    const handleColumnSelection = async (event: any) => {
        const newColumns = Number(event.target.value);
        setColumns(newColumns);
        console.log('Columns changed to:', newColumns);

        setLayout(layout);
        saveLayout(layout, newColumns);
    };

    const camerasToShow = isAdmin ? layout
        .map((id) => allCameras.find((c) => c.id === id))
        .filter((c): c is Camera => !!c) : cameras;

    return (
        <Box sx={{
            width: '100vw',
            height: '100vh',
            position: 'fixed',
            top: 0,
            left: 0,
            bgcolor: 'background.default',
            overflow: 'auto'
        }}>
            {error && (
                <Typography
                    sx={{
                        position: 'fixed',
                        top: '80px',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        color: 'red',
                        bgcolor: 'white',
                        p: 1,
                        borderRadius: 1,
                        zIndex: 5000,
                    }}
                >
                    {error}
                </Typography>
            )}
            {isLoading && (
                <Typography sx={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 1000 }}>
                    Loading cameras...
                </Typography>
            )}
            {!isLoading && cameras.length === 0 && (
                <Typography sx={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 1000 }}>
                    No cameras assigned.
                </Typography>
            )}
            {isAdmin && !isLoading && (
                <Box sx={{ display: 'flex', gap: 2, position: 'fixed', top: 80, right: 16, zIndex: 6000 }}>
                    <FormControl sx={{ minWidth: 250, bgcolor: 'white', borderRadius: 1, boxShadow: 3, p: 1 }}>
                        <InputLabel id="camera-select-label">Select Cameras</InputLabel>
                        <Select
                            labelId="camera-select-label"
                            multiple
                            value={selectedCameraIds}
                            onChange={handleCameraSelection}
                            label="Select Cameras"
                            renderValue={(selected) =>
                                selected
                                    .map((id) => allCameras.find((c) => c.id === id)?.name || '')
                                    .join(', ')
                            }
                            sx={{ bgcolor: 'white', minWidth: 200 }}
                        >
                            {allCameras.length === 0 ? (
                                <MenuItem disabled>No cameras available</MenuItem>
                            ) : (
                                allCameras.map((camera) => (
                                    <MenuItem key={camera.id} value={camera.id}>
                                        {camera.name}
                                    </MenuItem>
                                ))
                            )}
                        </Select>
                    </FormControl>
                    <FormControl sx={{ minWidth: 120, bgcolor: 'white', borderRadius: 1, boxShadow: 3, p: 1 }}>
                        <InputLabel id="columns-select-label">Columns</InputLabel>
                        <Select
                            labelId="columns-select-label"
                            value={columns}
                            onChange={handleColumnSelection}
                            label="Columns"
                            sx={{ bgcolor: 'white' }}
                        >
                            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => (
                                <MenuItem key={num} value={num}>
                                    {num}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>
            )}
            {!isLoading && cameras.length > 0 && (
                <InstanceIdContext.Provider value={instanceId}>
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: `repeat(${isAdmin ? columns : 1}, 1fr)`,
                            width: availableWidth,
                            maxHeight: `calc(100vh - ${156}px)`,
                            marginLeft: window.innerWidth > 840 ? '240px' : 0,
                            marginTop: '156px', // 64px (appbar) + 92px (control dropdown)
                        }}
                    >
                        {camerasToShow.map((camera) => {
                            return (
                                <CameraGridItem
                                    key={camera.id}
                                    camera={camera}
                                    isValidHttpUrl={isValidHttpUrl}
                                    isAdmin={isAdmin}
                                />
                            )
                        })}
                    </Box>
                </InstanceIdContext.Provider>
            )}
        </Box>
    )
}